<script setup lang="ts">
//#region import
import { ref } from 'vue';
import type { EmitsToListeners } from '@/types/core';
//#endregion

//#region type
interface Props {
  cloneName?: string
}

interface  Emits {
  'close': [],
  'success': [value: string]
}

export interface CloneDashboardParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>
}
//#endregion

//#region props
const props = withDefaults(defineProps<Props>(), {
  cloneName: '',
});
//#endregion

//#region emits
const emit = defineEmits<Emits>();
//#endregion

//#region data
const isVisible = ref(false);

const localCloneName = ref('');
//#endregion

//#region methods
const open = () => {
  localCloneName.value = props.cloneName;
  isVisible.value = true;

};

const handleClose = () => {
  emit('close');
  isVisible.value = false;
};
const handleSave = () => {
  emit('success', localCloneName.value);
  handleClose();
};
//#endregion

defineExpose({
  open,
  handleClose,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    class="clone-dashboard-popup"
    :title="$t('dashboards.confirm.clone_title')"
    :width="480"
    @close="handleClose"
  >
    <template v-slot:body>
      <div class="t:space-y-16">
        <div class="clone-dashboard-popup__text">
          <UiIcon
            :size="24"
            color="var(--text-positive)"
            name="exclamation-triangle"
            class="t:mr-12"
          />
          {{ $t('dashboards.confirm.clone_text') }}
        </div>

        <UiInput
          v-model="localCloneName"
          autosize
          :label="$t('dashboards.confirm.clone_label')"
        />
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:justify-end t:flex t:space-x-10">
        <UiButton
          type="negative"
          size="big"
          icon="times"
          @click="handleClose"
        >
          {{ $t('crm.buttons.cancel') }}
        </UiButton>
        <UiButton
          filled
          size="big"
          icon="clone"
          @click="handleSave"
        >
          {{ $t('dashboards.other.clone') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>

<style lang="scss">
.clone-dashboard-popup {
  &__text {
    display: flex;
    align-items: center;
    color: var(--text-body);
    font-size: 14px;
    line-height: 24px;
  }
}
</style>
